import {
    Invoice,
    InvoiceLineItem,
    InvoiceLineItemKind,
    InvoiceSortValue,
} from "@common/types/invoice";
import { lineItemsForByLineItemView } from "@common/helpers/lineItems";
import {
    InvoiceLineItemColumn,
    InvoiceLineItemView,
} from "@common/types/views/invoiceViews";
import { View } from "@common/types/views/views";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useCallback, useMemo } from "react";
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import classNames from "classnames";
import { createInvoiceLineItemsViewConfig } from "@components/invoices/views/invoiceLineItemsView/invoiceLineItemsViewConfig";
import { useAccrualEntityIdsWithLoading } from "@api/ledger.api";
import { useScheduleTemplates } from "@api/scheduleTemplate.api";
import { InvoiceLineItemsViewCallbacks } from "./types";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { useMultiLedgerColumns } from "@hooks/useMultiLedgerColumns";
import {
    buildLedgerAccountColumnIdByName,
    buildLedgerReceivableColumnIdByName,
    injectLedgerColumns,
    isLedgerReceivableColumnId,
} from "@lib/ledgerViewColumns";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { PeriodLockDatesProvider } from "@components/periodLock/PeriodLockDatesProvider";
import styles from "../InvoicesView.module.scss";

interface Props {
    view: InvoiceLineItemView;
    hasAnyAccrualLedgers: boolean;
    isSchedulesEnabled?: boolean;
    prioritizedScheduleTemplateId?: string;
    onCreateSchedule?: InvoiceLineItemsViewCallbacks["onCreateSchedule"];
    onScheduleApplied?: InvoiceLineItemsViewCallbacks["onScheduleApplied"];
    onRequestScheduleConfirm?: InvoiceLineItemsViewCallbacks["onRequestScheduleConfirm"];
    onRequestLifecycleAction?: InvoiceLineItemsViewCallbacks["onRequestLifecycleAction"];
    invoices: Invoice[] | null;
    selectedInvoiceId?: string;
    onInvoiceClick?: (invoice: Invoice) => void;
    sort?: InvoiceSortValue;
    onSortChange?: (sort: InvoiceSortValue) => void;
    emptyView?: () => React.ReactNode;
    footer?: React.ReactNode;
    className?: string;
    selectedLineItems?: Record<string, InvoiceLineItem>;
    hasSelectedByCurrentFilters?: boolean;
    onSelectedLineItemsChange?: (lineItems: InvoiceLineItem[]) => void;
}

const EMPTY_ROW_SELECTION: RowSelectionState = {};

export const InvoiceLineItemsView: React.FC<Props> = ({
    view,
    hasAnyAccrualLedgers,
    isSchedulesEnabled = false,
    prioritizedScheduleTemplateId,
    onCreateSchedule,
    onScheduleApplied,
    onRequestScheduleConfirm,
    onRequestLifecycleAction,
    invoices,
    selectedInvoiceId,
    onInvoiceClick,
    sort,
    onSortChange,
    emptyView = () => "No invoices found",
    footer,
    className,
    selectedLineItems,
    hasSelectedByCurrentFilters,
    onSelectedLineItemsChange,
}) => {
    const data = useMemo(
        () =>
            invoices
                ?.flatMap((invoice) =>
                    lineItemsForByLineItemView({
                        lines: invoice.lines,
                        adjustmentVisibility: {
                            [InvoiceLineItemKind.TAX]: invoice.isTaxPerLineItem
                                ? "each"
                                : "collapsed",
                            [InvoiceLineItemKind.DISCOUNT]:
                                invoice.isDiscountPerLineItem
                                    ? "each"
                                    : "collapsed",
                            [InvoiceLineItemKind.DEPOSIT]:
                                invoice.isDepositPerLineItem
                                    ? "each"
                                    : "collapsed",
                        },
                    }).map((line) => ({
                        ...line,
                        invoice,
                    })),
                )
                .filter(Boolean) ?? null,
        [invoices],
    ) as InvoiceLineItem[] | null;

    const handleRowClick = useCallback(
        (lineItem?: InvoiceLineItem) => {
            if (lineItem) {
                onInvoiceClick?.(lineItem.invoice!);
            }
        },
        [onInvoiceClick],
    );

    const handleFocusedRowChange = useCallback(
        (
            _index: number | null,
            row: InvoiceLineItem | null,
            source: "keyboard" | "click",
        ) => {
            const invoice = row?.invoice;
            if (
                source !== "keyboard" ||
                selectedInvoiceId === undefined ||
                !invoice ||
                invoice.id === selectedInvoiceId
            ) {
                return;
            }
            onInvoiceClick?.(invoice);
        },
        [selectedInvoiceId, onInvoiceClick],
    );

    const checkSiblings = useCallback(
        (invoiceLineItem: InvoiceLineItem) => {
            const index = data?.findIndex((i) => i.id === invoiceLineItem.id);

            if (index === undefined || !data) {
                return {
                    hasTopSibling: false,
                    hasBottomSibling: false,
                };
            }

            return {
                hasTopSibling:
                    index > 0 &&
                    data[index - 1].invoiceId === invoiceLineItem.invoiceId,
                hasBottomSibling:
                    index < data.length - 1 &&
                    data[index + 1].invoiceId === invoiceLineItem.invoiceId,
            };
        },
        [data],
    );

    const entityIds = useMemo(
        () => invoices?.map((invoice) => invoice.entityId) ?? [],
        [invoices],
    );
    const { accrualEntityIds, isLoading: isLoadingAccrualLedgers } =
        useAccrualEntityIdsWithLoading(entityIds);
    const multiLedgerLedgers = useMultiLedgerColumns(entityIds, {
        basis: AccountingLedgerBasis.accruals,
        uniqueByBasisAndName: true,
    });

    const scheduleTemplatesQuery = useScheduleTemplates({
        enabled: isSchedulesEnabled,
    });

    const tableMeta = useMemo(
        () => ({
            callbacks: {
                onInvoiceClick,
                checkSiblings,
                onCreateSchedule,
                onScheduleApplied,
                onRequestScheduleConfirm,
                onRequestLifecycleAction,
            },
            options: {
                scheduleTemplates: scheduleTemplatesQuery.data,
                prioritizedScheduleTemplateId,
            },
            accrualEntityIds,
            isLoadingAccrualLedgers,
        }),
        [
            onInvoiceClick,
            checkSiblings,
            onCreateSchedule,
            onScheduleApplied,
            onRequestScheduleConfirm,
            onRequestLifecycleAction,
            scheduleTemplatesQuery.data,
            prioritizedScheduleTemplateId,
            accrualEntityIds,
            isLoadingAccrualLedgers,
        ],
    );

    const rowSelection = useMemo(() => {
        if (!view.options?.bulkActions) {
            return EMPTY_ROW_SELECTION;
        }

        const selection: RowSelectionState = {};

        if (hasSelectedByCurrentFilters) {
            data?.forEach((lineItem) => {
                selection[lineItem.id] = true;
            });
        } else if (selectedLineItems) {
            Object.keys(selectedLineItems).forEach((id) => {
                selection[id] = true;
            });
        }

        return Object.keys(selection).length > 0
            ? selection
            : EMPTY_ROW_SELECTION;
    }, [
        data,
        hasSelectedByCurrentFilters,
        selectedLineItems,
        view.options?.bulkActions,
    ]);

    const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
        (updater) => {
            if (!data || !onSelectedLineItemsChange) {
                return;
            }

            const nextSelection =
                typeof updater === "function" ? updater(rowSelection) : updater;
            const selectedIds = Object.keys(nextSelection).filter(
                (id) => nextSelection[id],
            );

            const selected = data.filter((lineItem) =>
                selectedIds.includes(lineItem.id),
            );
            onSelectedLineItemsChange(selected);
        },
        [data, onSelectedLineItemsChange, rowSelection],
    );

    const enableRowSelection = useMemo(() => {
        if (!view.options?.bulkActions) {
            return false;
        }
        if (hasSelectedByCurrentFilters) {
            return false;
        }

        return true;
    }, [hasSelectedByCurrentFilters, view.options?.bulkActions]);

    const isClassesEnabled = useIsClassesEnabled();
    const invoiceLineItemsViewConfig = useMemo(
        () =>
            createInvoiceLineItemsViewConfig({
                hasAnyAccrualLedgers,
                isSchedulesEnabled,
                isClassesEnabled,
                multiLedgerLedgers,
            }),
        [
            hasAnyAccrualLedgers,
            isSchedulesEnabled,
            isClassesEnabled,
            multiLedgerLedgers,
        ],
    );
    const isDetailsPanelOpen = selectedInvoiceId !== undefined;
    const visibleView = useMemo(
        () =>
            injectLedgerColumns({
                view: {
                    ...view,
                    columns: view.columns.filter((column) => {
                        if (
                            !hasAnyAccrualLedgers &&
                            column.id === InvoiceLineItemColumn.STATUS
                        ) {
                            return false;
                        }
                        if (
                            !hasAnyAccrualLedgers &&
                            (column.id ===
                                InvoiceLineItemColumn.ACCOUNTS_RECEIVABLE ||
                                isLedgerReceivableColumnId(column.id))
                        ) {
                            return false;
                        }
                        if (
                            !isSchedulesEnabled &&
                            column.id === InvoiceLineItemColumn.SCHEDULE
                        ) {
                            return false;
                        }
                        if (
                            isDetailsPanelOpen &&
                            (column.id ===
                                InvoiceLineItemColumn.EXTERNAL_LINK ||
                                column.id === InvoiceLineItemColumn.ENTITY)
                        ) {
                            return false;
                        }
                        return true;
                    }),
                },
                ledgers: multiLedgerLedgers,
                replaceColumnIds: [
                    InvoiceLineItemColumn.ACCRUAL_ACCOUNT,
                    InvoiceLineItemColumn.ACCOUNTS_RECEIVABLE,
                ],
                buildColumnIds: (ledger) => [
                    buildLedgerAccountColumnIdByName(ledger),
                    buildLedgerReceivableColumnIdByName(ledger),
                ],
            }),
        [
            hasAnyAccrualLedgers,
            isSchedulesEnabled,
            view,
            multiLedgerLedgers,
            isDetailsPanelOpen,
        ],
    );

    return (
        <PeriodLockDatesProvider entityIds={entityIds}>
            <ViewTable
                className={classNames(styles.invoicesView, className)}
                data={data}
                view={visibleView as View}
                config={invoiceLineItemsViewConfig}
                selectedRowId={selectedInvoiceId}
                onRowClick={handleRowClick}
                onFocusedRowChange={handleFocusedRowChange}
                sort={sort}
                onSortChange={onSortChange}
                emptyView={emptyView}
                tableMeta={tableMeta}
                footer={footer}
                rowSelection={rowSelection}
                fixedTopOffsetPx
                onRowSelectionChange={handleRowSelectionChange}
                enableRowSelection={enableRowSelection}
                enableKeyboardShortcuts
            />
        </PeriodLockDatesProvider>
    );
};
